/**
 * The headset's half of the link numbers: what the operator actually saw, measured where they saw it.
 *
 * The arm times each controller frame as it lands, but only this page knows when a camera frame
 * reached the screen, whether the headset kept up with its own display, and how long the arm
 * took to answer a frame. So those are measured here and reported to the arm twice a second;
 * the arm records them into the episode and tells every page watching
 * (`pick_place.teleop.link_perf`).
 *
 * Every time that crosses the network is on the relay's shared clock (`TimeSyncHandle`), which
 * the arm reads too. A video frame carries its capture time on it, so the latency of a frame is
 * the clock when it is drawn minus that -- the same sum the SDK's own glass-to-glass figure does.
 */
import type { TimeSyncHandle } from "adamo/media";

import type { Perf } from "../adamo/types";

const FREEZE_MS = 100;      // no new video frame for this long is a freeze: the SDK's own stall trigger
const HITCH_MS = 100;       // one headset frame taking this long is a stall the operator sees
const PAUSE_MS = 2000;      // the page stops drawing altogether when the headset comes off: not a hitch
const EVENT_RESEND_MS = 5000;   // events ride along in every report this long; the arm keeps each once
const SENT_KEEP = 512;      // frames remembered per hand for the round trip

type Side = "left" | "right";
type Kind = "video_freeze" | "headset_hitch";
type Event = { id: number; kind: Kind; ms: number; at_us: number | null; at: number };

export type PerfReport = {
  type: "perf";
  operator_id: string;
  session: string;
  track: string;
  video_frames: number;
  video_dropped: number;
  headset_frames: number;
  headset_dropped: number;
  video_ms: number[];
  video_max_gap_ms: number | null;
  rtt_ms: number[];
  clock_synced: boolean;
  clock_error_ms: number | null;
  events: { id: number; kind: Kind; ms: number; at_us: number | null }[];
};

const round1 = (v: number) => Math.round(v * 10) / 10;

export class LinkMeter {
  /** The page's VR session: the arm reads counters as cumulative within one of these. */
  private readonly session = crypto.randomUUID();
  private readonly seqs: Record<Side, number> = { left: 0, right: 0 };
  private readonly sentAt: Record<Side, Map<number, number>> = { left: new Map(), right: new Map() };
  private videoMs: number[] = [];
  private rttMs: number[] = [];
  private videoFrames = 0;
  private videoDropped = 0;
  private headsetFrames = 0;
  private headsetDropped = 0;
  private lastVideo: number | null = null;
  private videoMaxGap: number | null = null;
  private lastXr: number | null = null;
  private events: Event[] = [];
  private nextEvent = 0;
  private lastStatus: unknown = null;
  /** Set once a packet shows the capture time is on the shared clock (the SDK's "V3" wire types).
   *  Before that a frame's timestamp is process-relative and means nothing here. */
  captureStamped = false;
  track = "";

  constructor(private readonly clock: () => TimeSyncHandle | null) {}

  /** The shared clock now, µs; null until this page has synced it. */
  fabricNowUs(): number | null {
    const c = this.clock();
    return c && c.synced() ? c.fabricNowUs() : null;
  }

  /** A controller frame is going out: number it and remember when. */
  nextSeq(side: Side, at = performance.now()): number {
    const seq = this.seqs[side]++;
    const sent = this.sentAt[side];
    sent.set(seq, at);
    sent.delete(seq - SENT_KEEP);
    return seq;
  }

  /** Once per headset frame, before drawing: a status that arrived since the last one closes a round trip. */
  statusSeen(perf: Perf | undefined, arrivedAt: number, me: string): void {
    if (perf === this.lastStatus) return;
    this.lastStatus = perf;
    const echo = perf?.echo;
    if (!echo || echo.source !== me || (echo.side !== "left" && echo.side !== "right")) return;
    const at = this.sentAt[echo.side].get(echo.seq);
    if (at === undefined) return;
    this.sentAt[echo.side].delete(echo.seq);   // one sample per frame, however many statuses repeat it
    const rtt = arrivedAt - at - echo.held_ms;
    if (rtt >= 0 && rtt < 10000) this.rttMs.push(round1(rtt));
  }

  /** A new video frame went onto the screen; `timestampUs` is the decoded frame's own. */
  videoShown(timestampUs: number, now = performance.now()): void {
    this.videoFrames += 1;
    if (this.lastVideo !== null) {
      const gap = now - this.lastVideo;
      this.videoMaxGap = Math.max(this.videoMaxGap ?? 0, gap);
      if (gap > FREEZE_MS) this.event("video_freeze", gap, now);
    }
    this.lastVideo = now;
    const fabric = this.fabricNowUs();
    if (this.captureStamped && fabric !== null && timestampUs > 1e15) {
      const ms = (fabric - timestampUs) / 1000;
      // slightly negative is the two clocks' error, not a frame from the future
      if (ms > -50 && ms < 10000) this.videoMs.push(round1(Math.max(0, ms)));
    }
  }

  /** The SDK threw a frame away before it was decoded (it arrived too late, or incomplete). */
  videoDrop(): void { this.videoDropped += 1; }

  /** Once per headset frame. `hz` is the display's rate when the browser says it. */
  xrFrame(now: number, hz: number | undefined): void {
    this.headsetFrames += 1;
    if (this.lastXr !== null) {
      const dt = now - this.lastXr;
      const period = 1000 / (hz && hz > 0 ? hz : 72);
      if (dt < PAUSE_MS) {
        if (dt > 1.5 * period) this.headsetDropped += Math.round(dt / period) - 1;
        if (dt > HITCH_MS) this.event("headset_hitch", dt, now);
      }
    }
    this.lastXr = now;
  }

  /** What goes to the arm; the per-frame samples are handed over and forgotten. */
  report(operatorId: string, now = performance.now()): PerfReport {
    const c = this.clock();
    this.events = this.events.filter((e) => now - e.at < EVENT_RESEND_MS);
    const out: PerfReport = {
      type: "perf",
      operator_id: operatorId,
      session: this.session,
      track: this.track,
      video_frames: this.videoFrames,
      video_dropped: this.videoDropped,
      headset_frames: this.headsetFrames,
      headset_dropped: this.headsetDropped,
      video_ms: this.videoMs,
      video_max_gap_ms: this.videoMaxGap === null ? null : round1(this.videoMaxGap),
      rtt_ms: this.rttMs,
      clock_synced: Boolean(c?.synced()),
      // the offset is only ever as good as half the fastest round trip it was measured over
      clock_error_ms: c?.synced() ? round1(c.minRttMs() / 2) : null,
      events: this.events.map(({ id, kind, ms, at_us }) => ({ id, kind, ms, at_us })),
    };
    this.videoMs = [];
    this.rttMs = [];
    this.videoMaxGap = null;
    return out;
  }

  private event(kind: Kind, ms: number, now: number): void {
    const fabric = this.fabricNowUs();
    this.events.push({ id: this.nextEvent++, kind, ms: round1(ms),
                       at_us: fabric === null ? null : fabric - ms * 1000, at: now });
  }
}
